// Print Dialog State Management Hook
// Sprint 2: PrintDialog Foundation

import { useState, useCallback, useEffect } from 'react';
import type { 
  PrintConfig, 
  PrintPreset, 
  PrintArea,
  PrintFormatOptions,
  PrintMetadata,
  PrintPageSize,
} from '@/app/types/print';
import type { TableConfig } from '@/app/types/table';
import {
  createDefaultPrintConfig,
  BUILT_IN_PRESETS,
} from '@/app/utils/printConstants';
import {
  loadPrintConfig,
  savePrintConfig,
  loadAllPresets,
  addCustomPreset,
  updateCustomPreset,
  deleteCustomPreset,
  applyPresetToConfig,
  createPresetFromConfig,
  validateBeforeSave,
  validatePreset,
} from '@/app/utils/printPersistence';
import { exportTable, generatePrintMetadata } from '@/app/utils/printUtils';
import { toast } from 'sonner';

export interface UsePrintDialogOptions {
  tableConfig: TableConfig;
  projectName: string;
  projectData?: {
    customAttributes?: Record<string, any>;
    createdAt?: Date;
    modifiedAt?: Date;
  };
  author?: string;
}

export function usePrintDialog(options: UsePrintDialogOptions) {
  const { tableConfig, projectName, projectData, author } = options;
  
  // ===== State =====
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<'area' | 'format' | 'metadata' | 'preview'>('area');
  const [config, setConfig] = useState<PrintConfig>(() => {
    const loaded = loadPrintConfig();
    if (loaded) {
      // Update metadata with current project info
      return {
        ...loaded,
        metadata: generatePrintMetadata(projectName, projectData, author),
      };
    }
    
    const defaultConfig = createDefaultPrintConfig();
    return {
      ...defaultConfig,
      metadata: generatePrintMetadata(projectName, projectData, author),
    };
  });
  
  const [presets, setPresets] = useState<PrintPreset[]>(() => loadAllPresets());
  const [selectedPresetId, setSelectedPresetId] = useState<string | null>(null);
  const [isExporting, setIsExporting] = useState(false);
  
  // ===== Auto-save config =====
  useEffect(() => {
    if (isOpen) {
      const validation = validateBeforeSave(config);
      if (validation.valid) {
        savePrintConfig(config);
      }
    }
  }, [config, isOpen]);
  
  // ===== Dialog Control =====
  const openDialog = useCallback(() => {
    // Refresh metadata when opening
    setConfig(prev => ({
      ...prev,
      metadata: generatePrintMetadata(projectName, projectData, author),
    }));
    setIsOpen(true);
  }, [projectName, projectData, author]);
  
  const closeDialog = useCallback(() => {
    setIsOpen(false);
    setActiveTab('area');
  }, []);
  
  // ===== Tab Navigation =====
  const goToNextTab = useCallback(() => {
    const tabs: Array<typeof activeTab> = ['area', 'format', 'metadata', 'preview'];
    const currentIndex = tabs.indexOf(activeTab);
    if (currentIndex < tabs.length - 1) {
      setActiveTab(tabs[currentIndex + 1]);
    }
  }, [activeTab]);
  
  const goToPreviousTab = useCallback(() => {
    const tabs: Array<typeof activeTab> = ['area', 'format', 'metadata', 'preview'];
    const currentIndex = tabs.indexOf(activeTab);
    if (currentIndex > 0) {
      setActiveTab(tabs[currentIndex - 1]);
    }
  }, [activeTab]);
  
  // ===== Config Updates =====
  const updateArea = useCallback((area: Partial<PrintArea>) => {
    setConfig(prev => ({
      ...prev,
      area: { ...prev.area, ...area },
    }));
  }, []);
  
  const updateFormat = useCallback((format: Partial<PrintFormatOptions>) => {
    setConfig(prev => ({
      ...prev,
      format: { ...prev.format, ...format },
    }));
  }, []);
  
  const updatePDFOptions = useCallback((pdfOptions: Partial<PrintFormatOptions['pdf']>) => {
    setConfig(prev => ({
      ...prev,
      format: {
        ...prev.format,
        pdf: prev.format.pdf ? { ...prev.format.pdf, ...pdfOptions } : undefined,
      },
    }));
  }, []);
  
  const updateExcelOptions = useCallback((excelOptions: Partial<PrintFormatOptions['excel']>) => {
    setConfig(prev => ({
      ...prev,
      format: {
        ...prev.format,
        excel: prev.format.excel ? { ...prev.format.excel, ...excelOptions } : undefined,
      },
    }));
  }, []);
  
  const updateMetadata = useCallback((metadata: Partial<PrintMetadata>) => {
    setConfig(prev => ({
      ...prev,
      metadata: { ...prev.metadata, ...metadata },
    }));
  }, []);
  
  const updatePreviewZoom = useCallback((zoom: number) => {
    setConfig(prev => ({
      ...prev,
      preview: { ...prev.preview, zoom },
    }));
  }, []);
  
  const updatePageSize = useCallback((pageSize: PrintPageSize) => {
    setConfig(prev => ({
      ...prev,
      format: {
        ...prev.format,
        pdf: prev.format.pdf ? { ...prev.format.pdf, pageSize } : undefined,
      },
    }));
  }, []);
  
  // ===== Preset Management =====
  const loadPreset = useCallback((presetId: string) => {
    const preset = presets.find(p => p.id === presetId);
    if (!preset) {
      toast.error('Preset not found');
      return;
    }
    
    const newConfig = applyPresetToConfig(config, preset);
    setConfig(newConfig);
    setSelectedPresetId(presetId);
    toast.success(`Preset "${preset.name}" loaded`);
  }, [presets, config]);
  
  const saveAsPreset = useCallback((name: string, description?: string) => {
    try {
      const newPreset = createPresetFromConfig(config, name, description);
      const validation = validatePreset(newPreset);
      
      if (!validation.valid) {
        toast.error(validation.errors.join(', '));
        return;
      }
      
      addCustomPreset(newPreset);
      setPresets(loadAllPresets());
      setSelectedPresetId(newPreset.id);
      toast.success(`Preset "${name}" saved`);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to save preset');
    }
  }, [config]);
  
  const deletePreset = useCallback((presetId: string) => {
    try {
      const preset = presets.find(p => p.id === presetId);
      if (!preset) {
        toast.error('Preset not found');
        return;
      }
      
      if (preset.isBuiltIn) {
        toast.error('Cannot delete built-in presets');
        return;
      }
      
      deleteCustomPreset(presetId);
      setPresets(loadAllPresets());
      
      if (selectedPresetId === presetId) {
        setSelectedPresetId(null);
      }
      
      toast.success(`Preset "${preset.name}" deleted`);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to delete preset');
    }
  }, [presets, selectedPresetId]);
  
  const updatePreset = useCallback((presetId: string, updates: Partial<PrintPreset>) => {
    try {
      updateCustomPreset(presetId, updates);
      setPresets(loadAllPresets());
      toast.success('Preset updated');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to update preset');
    }
  }, []);
  
  // ===== Export Actions =====
  const handleExport = useCallback(async () => {
    setIsExporting(true);
    
    try {
      // Update metadata with current timestamp
      const exportConfig: PrintConfig = {
        ...config,
        metadata: {
          ...config.metadata,
          datePrinted: new Date(),
        },
      };
      
      await exportTable(exportConfig, tableConfig);
      
      toast.success(
        `Table exported as ${config.format.format === 'pdf' ? 'PDF' : 'CSV'}`,
        { duration: 3000 }
      );
      
      // Close dialog after successful export
      setTimeout(() => {
        closeDialog();
      }, 500);
    } catch (error) {
      console.error('Export failed:', error);
      toast.error(error instanceof Error ? error.message : 'Export failed');
    } finally {
      setIsExporting(false);
    }
  }, [config, tableConfig, closeDialog]);
  
  const quickExportPDF = useCallback(async () => {
    const quickConfig = createDefaultPrintConfig();
    quickConfig.metadata = generatePrintMetadata(projectName, projectData, author);
    
    try {
      await exportTable(quickConfig, tableConfig);
      toast.success('Table exported as PDF');
    } catch (error) {
      console.error('Quick export failed:', error);
      toast.error('Export failed');
    }
  }, [tableConfig, projectName, projectData, author]);
  
  const quickExportCSV = useCallback(async () => {
    const quickConfig = createDefaultPrintConfig();
    quickConfig.metadata = generatePrintMetadata(projectName, projectData, author);
    quickConfig.format = {
      format: 'excel-csv',
      pdf: quickConfig.format.pdf,
      excel: quickConfig.format.excel,
    };
    
    try {
      await exportTable(quickConfig, tableConfig);
      toast.success('Table exported as CSV');
    } catch (error) {
      console.error('Quick export failed:', error);
      toast.error('Export failed');
    }
  }, [tableConfig, projectName, projectData, author]);
  
  // ===== Reset =====
  const resetToDefaults = useCallback(() => {
    const defaultConfig = createDefaultPrintConfig();
    setConfig({
      ...defaultConfig,
      metadata: generatePrintMetadata(projectName, projectData, author),
    });
    setSelectedPresetId(null);
    toast.info('Reset to default settings');
  }, [projectName, projectData, author]);
  
  return {
    // Dialog state
    isOpen,
    activeTab,
    config,
    isExporting,
    
    // Preset state
    presets,
    selectedPresetId,
    
    // Dialog control
    openDialog,
    closeDialog,
    setActiveTab,
    goToNextTab,
    goToPreviousTab,
    
    // Config updates
    updateArea,
    updateFormat,
    updatePDFOptions,
    updateExcelOptions,
    updateMetadata,
    updatePreviewZoom,
    updatePageSize,
    
    // Preset management
    loadPreset,
    saveAsPreset,
    deletePreset,
    updatePreset,
    
    // Export actions
    handleExport,
    quickExportPDF,
    quickExportCSV,
    
    // Reset
    resetToDefaults,
  };
}